import { useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  useGetRoadNotesQuery,
  useCreateRoadNoteMutation,
  useUpdateRoadNoteMutation,
  useDeleteRoadNoteMutation,
  useGetChannelVideosQuery,
} from "../../features/api/youtubeApi";
import FuelLoading from "../../pages/FuelLoading";
import "./adminNotesForm.scss";
import { useToast } from "../toast/ToastContext";
import ConfirmModal from "../modal/ConfirmModal";
import AdminHeader from "../layout/AdminHeader";

// Clean toolbar modules configuration map for your traveler logs
const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

export default function AdminRoadNotes() {
  const { showToast } = useToast();

  // 🗺️ DATA FETCH PIPELINES
  const {
    data: notesData,
    isLoading: notesLoading,
    isError: notesError,
  } = useGetRoadNotesQuery();
  const notes = notesData?.items || [];

  const { data: videosData, isLoading: videosLoading } =
    useGetChannelVideosQuery({ search: "", page: 1, limit: 50 });
  const videos = videosData?.items || [];

  // Form Field States
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState(""); // 🟢 NEW STATE: Tracks custom short snippets
  const [content, setContent] = useState("");
  const [videoRefs, setVideoRefs] = useState([]);
  const [videoSearch, setVideoSearch] = useState("");
  const [editingId, setEditingId] = useState(null);

  // MODAL INTERACTIVE STATE TRACKERS
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetTitle: "",
  });

  const [createNote, { isLoading: isCreating }] = useCreateRoadNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateRoadNoteMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteRoadNoteMutation();
  const isMutating = isCreating || isUpdating || isDeleting;

  const resetForm = () => {
    setTitle("");
    setExcerpt(""); // 🟢 Reset field tracker
    setContent("");
    setVideoRefs([]);
    setVideoSearch("");
    setEditingId(null);
  };

  const handleEdit = (note) => {
    setEditingId(note._id);
    setTitle(note.title || "");
    setExcerpt(note.excerpt || ""); // 🟢 Populate field when editing
    setContent(note.content || "");
    setVideoRefs(note.videoRefs || []);
  };

  const toggleVideo = (video) => {
    setVideoRefs((prev) => {
      const exists = prev.find((v) => v._id === video._id);
      if (exists) return prev.filter((v) => v._id !== video._id);
      return [...prev, video];
    });
  };

  const handleDeleteTrigger = (id, noteTitle) => {
    setModalConfig({ isOpen: true, targetId: id, targetTitle: noteTitle });
  };

  const executeDeletePurge = async () => {
    const { targetId, targetTitle } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetTitle: "" });

    try {
      await deleteNote(targetId).unwrap();
      showToast(
        `"${targetTitle}" successfully purged from database logs.`,
        "success"
      );
      if (editingId === targetId) resetForm();
    } catch (err) {
      showToast(
        "Failed to remove target entry from backend database storage.",
        "error"
      );
    }
  };

  const filteredVideos = useMemo(() => {
    if (!videoSearch) return videos.slice(0, 50);
    return videos
      .filter((v) => v.title?.toLowerCase().includes(videoSearch.toLowerCase()))
      .slice(0, 50);
  }, [videoSearch, videos]);

  const submit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim() || content === "<p><br></p>") {
      showToast(
        "Please write a title header and message body narrative first.",
        "error"
      );
      return;
    }

    const payload = {
      title: title.trim(),
      excerpt: excerpt.trim(), // 🟢 Pass excerpt to payload bundle
      content: content.trim(),
      videoRefs: videoRefs.map((v) => v._id),
    };

    try {
      if (editingId) {
        await updateNote({ id: editingId, ...payload }).unwrap();
        showToast(
          "Journal parameters updated and synced across data lanes!",
          "success"
        );
      } else {
        await createNote(payload).unwrap();
        showToast("New road note published live to reader feeds!", "success");
      }
      resetForm();
    } catch (err) {
      showToast(
        err?.data?.message || "Failed to commit note records pipeline.",
        "error"
      );
    }
  };

  if (notesLoading) {
    return (
      <div className="admin-notes-loading-box">
        <FuelLoading text="Opening journal file vaults..." />
      </div>
    );
  }

  if (notesError) {
    return (
      <div className="admin-notes-error-slate">
        ⚠️ Secure data records access denied or server is offline.
      </div>
    );
  }
  return (
    <div className="admin-road-notes fuel-fade-in">
      {/* 🟢 CALL DYNAMICALLY WITH CUSTOM PROPERTIES */}
      <AdminHeader
        title="Road Notes Workspace"
        description="Compose traveling thoughts, map out route highlights, and link driving clips directly down your public reader feeds."
        badgeText="Travel Log"
      />
      {/* 🟢 FLOATING MODAL INTERFACE TARGET */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title="Purge Journal Entry"
        message={`Are you absolutely sure you want to permanently delete "${modalConfig.targetTitle}" from public indexes? This action cannot be reversed.`}
        onConfirm={executeDeletePurge}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetTitle: "" })
        }
      />

      {isMutating && (
        <FuelLoading
          text="Synchronizing database registry tracks..."
          fullScreen
        />
      )}
      <div className="admin-notes-layout-grid">
        {/* ===== LEFT PANEL: INTERACTIVE COMPOSITION EDITOR WRAPPER ===== */}
        <section className="notes-editor-panel">
          <h2>{editingId ? "✏️ Edit Road Note" : "Log New Road Note"}</h2>
          <p>
            Compose thoughts, route highlights, and attach relevant YouTube
            driving videos maps.
          </p>

          <form onSubmit={submit} className="notes-editor-form">
            {/* Title Header Input wrapper row */}
            <div className="notes-field-group">
              <label htmlFor="note-title">Note Header Title *</label>
              <input
                id="note-title"
                className="title-input"
                type="text"
                placeholder="e.g. Navigating the Backroads of Utah"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isMutating}
              />
            </div>

            <div className="notes-field-group">
              <label htmlFor="note-excerpt">
                Card Summary Teaser Excerpt (Optional)
              </label>
              <input
                id="note-excerpt"
                className="title-input"
                type="text"
                placeholder="Brief summary sentence snippet... Leave empty to auto-extract from body narrative blocks."
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                maxLength={180}
                disabled={isMutating}
              />
            </div>

            {/* React-Quill core writing canvas block */}
            <div className="notes-field-group quill-wrapper-block">
              <span className="quill-label-text">Journal Body Narrative *</span>
              <ReactQuill
                className="quill-editor"
                theme="snow"
                value={content}
                onChange={setContent}
                placeholder="Write the road note details here..."
                modules={quillModules}
                readOnly={isMutating}
              />
            </div>

            {/* INTERACTIVE VIDEO RESOURCE CONNECTION EXTENSION ZONE */}
            <div className="video-selector-zone">
              <h3>Connect Travel Clips</h3>
              <p>
                Link recorded driving videos segments directly inside this
                narrative log frame.
              </p>

              {/* Pill tags container tray layout */}
              {videoRefs.length > 0 && (
                <div className="selected-videos-tray">
                  {videoRefs.map((v) => (
                    <span key={v._id} className="video-tag-pill">
                      FaVideo {v.title}
                      <button
                        type="button"
                        onClick={() => toggleVideo(v)}
                        aria-label="Remove video tag mapping"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <input
                type="text"
                className="video-search-bar"
                placeholder="🔍 Filter clips library by title keyword..."
                value={videoSearch}
                onChange={(e) => setVideoSearch(e.target.value)}
                disabled={isMutating}
              />

              {/* Vertical scrolling list stack row */}
              <div className="video-scroll-matrix">
                {videosLoading ? (
                  <p className="inline-loader-text">
                    Opening channel video clip registers...
                  </p>
                ) : filteredVideos.length > 0 ? (
                  filteredVideos.map((v) => {
                    const isSelected = videoRefs.some(
                      (ref) => ref._id === v._id
                    );
                    return (
                      <button
                        key={v._id}
                        type="button"
                        className={`video-item-row ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => toggleVideo(v)}
                        disabled={isMutating}
                      >
                        <div className="thumb-wrapper">
                          <img
                            src={v.thumbnails?.medium?.url}
                            alt=""
                            loading="lazy"
                          />
                          {isSelected && <span className="check-badge">✓</span>}
                        </div>
                        <span className="title-text">{v.title}</span>
                      </button>
                    );
                  })
                ) : (
                  <p className="inline-loader-text">
                    No matching channel video logs discovered.
                  </p>
                )}
              </div>
            </div>

            {/* Control buttons action bar footer row */}
            <div className="editor-actions">
              <button
                type="submit"
                className="fuel-primary-btn submit-note-btn"
                disabled={isMutating}
              >
                {editingId ? "Save Document Overwrites" : "Broadcast Road Note"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="cancel-ghost-btn"
                  onClick={resetForm}
                  disabled={isMutating}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ===== RIGHT PANEL: SAVED ROAD NOTES INDEX FEED MATRIX ===== */}
        <section className="notes-list-panel">
          <div className="list-panel-header">
            <h3>Logged Notes Library ({notes.length})</h3>
          </div>

          <div className="notes-scroll-stack">
            {notes.length === 0 ? (
              <p className="empty-notes-text">
                No travel entries saved inside database tracks yet.
              </p>
            ) : (
              notes.map((note) => (
                <div
                  key={note._id}
                  className={`note-row-card ${
                    editingId === note._id ? "note-row-card--active" : ""
                  }`}
                >
                  <div className="note-row-card__meta">
                    <strong>{note.title}</strong>
                    <time>
                      🗓️ {new Date(note.createdAt).toLocaleDateString()}
                    </time>
                  </div>

                  <div className="note-row-card__controls">
                    <button
                      type="button"
                      className="ctrl-action-btn edit"
                      onClick={() => handleEdit(note)}
                      disabled={isMutating}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      type="button"
                      className="ctrl-action-btn delete"
                      onClick={() => handleDeleteTrigger(note._id, note.title)}
                      disabled={isMutating}
                    >
                      🗑️ Purge
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
